// src/pages/TicketDesignTemplate/modals/ApproveConfirmModal.tsx
import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { TicketDesignTemplate, DESIGN_UNIT_LABELS } from '../types';
import moment from 'moment';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_design_template');

interface ApproveConfirmModalProps {
  isOpen: boolean;
  template: TicketDesignTemplate | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ApproveConfirmModal: React.FC<ApproveConfirmModalProps> = ({
  isOpen,
  template,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const location = useLocation();
  if (!template) return null;

  // Format file size
  const formatFileSize = (bytes?: number | null) => {
    if (!bytes) return st('chua_co_file_text');
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        <i className="ri-check-double-line text-success me-2"></i>
        {st('xac_nhan_phe_duyet_title')}
      </ModalHeader>
      <ModalBody>
        <div className="alert alert-info">
          <i className="ri-information-line me-2"></i>
          <strong>{st('note_title')}</strong> {st('sau_khi_phe_duyet_mau_thiet_text')}
        </div>
        
        <h6 className="mb-3">{st('thong_tin_mau_thiet_ke_title')}</h6>
        
        <div className="table-responsive">
          <table className="table table-bordered table-sm">
            <tbody>
              <tr>
                <td width="30%" className="fw-semibold bg-light">{st('ma_mau_text')}</td>
                <td>{template.templateCode}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('template_name_label')}</td>
                <td>{template.templateName}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('design_unit_label')}</td>
                <td>{DESIGN_UNIT_LABELS[template.designUnit || ''] || template.designUnit}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('issuance_period_label')}</td>
                <td>{template.issuancePeriod}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('trang_thai_hien_tai_text')}</td>
                <td>
                  <span className="badge bg-warning">{template.statusDisplayName}</span>
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('design_file_label')}</td>
                <td>
                  {template.fileName ? (
                    <>
                      <i className="ri-file-line me-1"></i>
                      {template.fileName} ({formatFileSize(template.fileSize)})
                    </>
                  ) : (
                    <span className="text-muted">{st('chua_co_file_text')}</span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('nguoi_tao_text')}</td>
                <td>{template.uploadedByName || '-'}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ngay_tao_text')}</td>
                <td>{template.createdAt ? moment(template.createdAt).format('DD/MM/YYYY HH:mm') : '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="alert alert-success mt-3 mb-0">
          <i className="ri-checkbox-circle-line me-2"></i>
          <strong>{st('sau_khi_phe_duyet_text')}</strong>
          <ul className="mb-0 mt-2">
            <li>{st('trang_thai_se_chuyen_thanh_da_text')}</li>
            <li>{st('thong_tin_nguoi_duyet_va_thoi_text')}</li>
            <li>{st('khong_the_chinh_sua_hoac_xoa_text')}</li>
            <li>{st('mau_se_san_sang_de_su_text')}</li>
          </ul>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={loading}>
          {st('huy_button')}
        </Button>
        {PageActionPermission.canDisplay(location.pathname, "button-approve-confirm") && (
          <Button
            color="success"
            data-action-code="button-approve-confirm"
            data-action-name="Xác nhận duyệt mẫu vé"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {st('dang_phe_duyet_text')}
              </>
            ) : (
              <>
                <i className="ri-check-line me-1"></i>
                {st('phe_duyet_text')}
              </>
            )}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ApproveConfirmModal;